import React, { useEffect, useState, useReducer } from "react";
import {
  Box,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Typography,
} from "@material-ui/core";
import { Link } from "react-router-dom";
import { FormattedMessage } from "react-intl";
import IconManager from "@material-ui/icons/Group";
import IconAgent from "@material-ui/icons/Person";
import IconCustomer from "@material-ui/icons/PersonOutline";
import IconCheck from "@material-ui/icons/Check";
import IconUncheck from "@material-ui/icons/Close";
import IconArrowDown from "@material-ui/icons/KeyboardArrowDown";
import IconArrowUp from "@material-ui/icons/KeyboardArrowUp";
import IconInfoOutlined from "@material-ui/icons/InfoOutlined";
import IconInfo from "@material-ui/icons/Info";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";

import { Roles } from "../../../../util";
import UserContractDetails from "./UserContractsDetails";
import { TOGGLE_ALL_ENUM } from "../../../ManagersAndAgentsHierarchy";

const UserListItem = ({ currentUser, user, classes, lines, bgIntensity: bgIntensityProps = 50, toggleAll, enableInfoValues, isRoot }) => {
  const [, forceUpdate] = useReducer((x) => x + 1, 0);
  const [openInfo, setOpenInfo] = useState(false);
  const [isListOpen, setListOpen] = useState(
    () => !enableInfoValues && isRoot
  );
  const [bgIntensity, setBgIntensity] = useState(bgIntensityProps);
  const hasCCOs = !!user.ccos.length;
  const hasManagers = !!user.managers.length;
  const hasAgents = !!user.agents.length;
  const hasCustomers = !!user.customers.length;
  const hasSalesDirectors = !!user.salesDirectors.length;
  const hasRegionalManagers = !!user.regionalManagers.length;
  const aptColor = { true: "green", false: "red" };

  useEffect(() => {
    if (toggleAll === TOGGLE_ALL_ENUM.open) {
      setListOpen(true);
      return;
    }
    if (toggleAll === TOGGLE_ALL_ENUM.close) {
      setListOpen(false);
    }
  }, [toggleAll]);

  useEffect(() => {
    let bgIntensity = bgIntensityProps - 10;
    if (bgIntensity < 0) {
      bgIntensity = 0;
    }
    setBgIntensity(bgIntensity);
  }, [bgIntensityProps]);

  const handleToggleListOpen = () => {
    setListOpen(!isListOpen);
  };

  const hasUsersBelow = React.useMemo(
    () => hasCCOs || hasSalesDirectors || hasRegionalManagers || hasManagers || hasAgents || hasCustomers,
    [hasCCOs, hasSalesDirectors, hasRegionalManagers, hasManagers, hasAgents, hasCustomers]
  );

  const canNavigate = React.useMemo(() => !enableInfoValues && !isRoot && currentUser.id !== user.id, [enableInfoValues, isRoot, currentUser.id, user.id]);

  if (isRoot && !hasUsersBelow)
    return (
      <Typography variant="body1" align="center" color="textSecondary">
        <FormattedMessage id="app.users.bellow.none" />
      </Typography>
    );

  return (
    <>
      {lines && (
        <hr style={{ border: "1px solid #333", padding: 0, margin: 0 }} />
      )}
      <div
        style={{
          borderLeft: "1px solid #333",
          backgroundColor: isListOpen
            ? `rgb(${bgIntensity},${bgIntensity},${bgIntensity})`
            : "unset",
        }}
      >
        {!isRoot && (
          <ListItem
            button={hasUsersBelow}
            onClick={() => {
              if (hasUsersBelow) {
                handleToggleListOpen();
                forceUpdate();
              }
            }}
          >
            <ListItemIcon>
              <div>
                {user.role === Roles.Customer ? (
                  <IconCustomer classes={{ root: classes.iconCustomer }} />
                ) : user.isSalesDirector || user.isRegionalManager || user.isCCO ? (
                  <IconManager classes={{ root: classes.iconManager }} />
                ) : user.isManager ? (
                  <IconManager />
                ) : (
                  <IconAgent classes={{ root: classes.iconAgent }} />
                )}
                {user.role === Roles.Agent &&
                  (user.contractAgentStatus === "ACTIVE" ? (
                    <IconCheck
                      style={{
                        fontSize: "100%",
                        color: "lime",
                        marginLeft: -6,
                        marginTop: -2,
                        position: "absolute",
                      }}
                    />
                  ) : (
                    <IconUncheck
                      style={{
                        fontSize: "100%",
                        color: "red",
                        marginLeft: -6,
                        marginTop: -2,
                        position: "absolute",
                      }}
                    />
                  ))}
              </div>
            </ListItemIcon>
            <ListItemText
              primary={
                <>
                  <Typography>
                    {user.name} <small style={{ color: "#999" }}>({user.id})</small>{" "}
                  </Typography>
                  {enableInfoValues && (
                    <Typography
                      variant="caption"
                      classes={{ root: classes.contractCount }}
                    >
                      <b>
                        {!user.contractsCount ? (
                          ""
                        ) : (
                          <FormattedMessage
                            id={"app.contracts.activeAndCount"}
                            values={{
                              active: user.activeContractsCount,
                              count: user.contractsCount,
                            }}
                          />
                        )}
                      </b>
                    </Typography>
                  )}
                </>
              }
            />
            <ListItemSecondaryAction>
              {hasUsersBelow && (
                <IconButton
                  size="small"
                  onClick={() => {
                    handleToggleListOpen();
                    forceUpdate();
                  }}
                >
                  {isListOpen ? <IconArrowUp /> : <IconArrowDown />}
                </IconButton>
              )}
              {enableInfoValues ? (
                <IconButton
                  size="small"
                  onClick={() => {
                    setOpenInfo(!openInfo);
                  }}
                >
                  {openInfo ? <IconInfo style={{ color: aptColor[user.apt] }} /> : <IconInfoOutlined style={{ color: aptColor[user.apt] }} />}
                </IconButton>
              ) : (
                canNavigate && (
                  <IconButton
                    size="small"
                    component={canNavigate ? Link : "span"}
                    to={{ pathname: `/user-detail/${user.id}`, state: { backTo: "/team-management/team" } }}
                    style={{ marginLeft: 4 }}
                  >
                    <OpenInNewIcon />
                  </IconButton>
                )
              )}
            </ListItemSecondaryAction>
          </ListItem>
        )}
        {openInfo && (
          <UserContractDetails
            user={user}
            currentUser={currentUser}
            open={openInfo}
          />
        )}
        <Collapse
          in={isListOpen}
          timeout="auto"
          mountOnEnter
          unmountOnExit
          style={{ marginLeft: isRoot ? 0 : "5%" }}
        >
          {hasCCOs && (
            <>
              <Typography
                variant="caption"
                classes={{ root: classes.listTitle }}
              >
                <FormattedMessage id="app.enuns.levelName.CCO" />
              </Typography>
              <List>
                {user.ccos.map((cco) => (
                  <UserListItem
                    key={cco.id}
                    currentUser={currentUser}
                    user={cco}
                    classes={classes}
                    bgIntensity={bgIntensity}
                    enableInfoValues={enableInfoValues}
                    toggleAll={toggleAll}
                  />
                ))}
              </List>
            </>
          )}
          {hasSalesDirectors && (
            <>
              <Typography
                variant="caption"
                classes={{ root: classes.listTitle }}
              >
                <FormattedMessage id="app.enuns.levelName.SALES_DIRECTOR" />
              </Typography>
              <List>
                {user.salesDirectors.map((salesDirector) => (
                  <UserListItem
                    key={salesDirector.id}
                    currentUser={currentUser}
                    user={salesDirector}
                    classes={classes}
                    bgIntensity={bgIntensity}
                    enableInfoValues={enableInfoValues}
                    toggleAll={toggleAll}
                  />
                ))}
              </List>
            </>
          )}
          {hasRegionalManagers && (
            <>
              <Typography
                variant="caption"
                classes={{ root: classes.listTitle }}
              >
                <FormattedMessage id="app.enuns.levelName.REGIONAL_MANAGER" />
              </Typography>
              <List>
                {user.regionalManagers.map((regionalManager) => (
                  <UserListItem
                    key={regionalManager.id}
                    currentUser={currentUser}
                    user={regionalManager}
                    classes={classes}
                    bgIntensity={bgIntensity}
                    enableInfoValues={enableInfoValues}
                    toggleAll={toggleAll}
                  />
                ))}
              </List>
            </>
          )}
          {hasManagers && (
            <>
              <Typography
                variant="caption"
                classes={{ root: classes.listTitle }}
              >
                <FormattedMessage id="app.managers" />
              </Typography>
              <List>
                {user.managers.map((manager) => (
                  <UserListItem
                    key={manager.id}
                    currentUser={currentUser}
                    user={manager}
                    classes={classes}
                    bgIntensity={bgIntensity}
                    enableInfoValues={enableInfoValues}
                    toggleAll={toggleAll}
                  />
                ))}
              </List>
            </>
          )}
          {hasAgents && (
            <>
              <Typography
                variant="caption"
                classes={{ root: classes.listTitle }}
              >
                <FormattedMessage id="app.agents" />
              </Typography>
              <List>
                {user.agents.map((agent) => (
                  <UserListItem
                    key={agent.id}
                    currentUser={currentUser}
                    user={agent}
                    classes={classes}
                    bgIntensity={bgIntensity}
                    enableInfoValues={enableInfoValues}
                    toggleAll={toggleAll}
                  />
                ))}
              </List>
            </>
          )}
          {hasCustomers && (
            <>
              <HierarchySection
                bgIntensity={bgIntensity}
                classes={classes}
                currentUser={currentUser}
                enableInfoValues={enableInfoValues}
                enableCollapse
                defaultOpen={isListOpen}
                users={user.customers}
                title="app.customers"
              />
            </>
          )}
        </Collapse>
      </div>
    </>
  );
};

const HierarchySection = ({ bgIntensity, classes, currentUser, enableInfoValues, enableCollapse, defaultOpen, title, users }) => {
  const [isOpen, setOpen] = useState(!enableCollapse && defaultOpen);

  const handleOpen = () => {
    if (enableCollapse) setOpen((prev) => !prev);
  };

  return (
    <>
      <Box
        style={{
          cursor: enableCollapse ? "pointer" : "inherit",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          paddingTop: 4,
          paddingBottom: 4,
        }}
        onClick={handleOpen}
      >
        <Typography
          variant="caption"
          classes={{ root: classes.listTitle }}
        >
          <FormattedMessage id={title} />
        </Typography>
        {enableCollapse && (
          <IconButton
            size="small"
            style={{
              transform: isOpen ? "rotate(0deg)" : "rotate(180deg)",
              transition: "transform 0.2s ease",
              marginRight: 16,
            }}
          >
            <IconArrowUp style={{ color: "#666" }} />
          </IconButton>
        )}
      </Box>
      <Collapse
        in={isOpen}
        timeout="auto"
        unmountOnExit
      >
        <List>
          {users.map((u) => (
            <UserListItem
              key={u.id}
              currentUser={currentUser}
              user={u}
              classes={classes}
              bgIntensity={bgIntensity}
              enableInfoValues={enableInfoValues}
            />
          ))}
        </List>
      </Collapse>
    </>
  );
};

export default React.memo(UserListItem);
